import { gql } from '@apollo/client';
import { get } from 'lodash-es';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

const DEFAULT = 'default';

export function buildSegmentRatingsPath({ makeSlug, modelSlug, year }) {
  return (
    !!makeSlug && !!modelSlug && !!year && `makes["${makeSlug}"].models["${modelSlug}"].years["${year}"].segmentRatings`
  );
}

export function buildBadgesPath({ makeSlug, modelSlug, year, submodelSlug = DEFAULT }) {
  return `makes["${makeSlug}"].models["${modelSlug}"].years["${year}"].submodels["${submodelSlug}"].badges`;
}

export const TypeRankingsFeaturesModel = createModelSegment('typeRankingsFeatures', [
  /**
   * @see buildSegmentRatingsPath
   * @returns {Array<SegmentRating>}
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].segmentRatings',
    resolve({ makeSlug, modelSlug, year }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (modelYear === null) {
        return Promise.resolve(null);
      }

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!, $year: Int!) {
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                editorialSegmentRatings {
                  baseStyleId
                  badges
                  rank
                  slugRankedSubmodel
                  editorialSegment {
                    edmundsTypeCategory
                    id
                    displayName
                    subsegment
                    segmentRatings(filter: { ratingMode: NEW }, pagesize: 100, rankRange: { start: 1, end: 100 }) {
                      baseStyleId
                      modelYear {
                        makeName
                        makeSlug
                        modelName
                        modelSlug
                        year
                      }
                      rankedSubmodel
                      slugRankedSubmodel
                      rank
                      rating
                      priceRange {
                        min
                      }
                    }
                  }
                }
              }
            }
          `,
          {
            makeSlug,
            modelSlug,
            year: modelYear,
          }
        )
        .then(response => get(response, 'modelYears[0].editorialSegmentRatings', null));
    },
  },
  /**
   * @see buildBadgesPath
   * @returns {Badges}
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].submodels["{submodelSlug}"].badges',
    resolve({ makeSlug, modelSlug, year, submodelSlug }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (modelYear === null) {
        return Promise.resolve(null);
      }

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!, $year: Int!) {
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                editorialSegmentRatings {
                  badges
                  slugRankedSubmodel
                }
              }
            }
          `,
          {
            makeSlug,
            modelSlug,
            year: modelYear,
          }
        )
        .then(response => {
          const editorialSegmentRatings = get(response, 'modelYears[0].editorialSegmentRatings', []);
          const currentSegment =
            editorialSegmentRatings.find(({ slugRankedSubmodel }) => slugRankedSubmodel === submodelSlug) ||
            editorialSegmentRatings.find(({ slugRankedSubmodel }) => !slugRankedSubmodel);

          return get(currentSegment, 'badges', null);
        });
    },
  },
]);
